/**
 * Care-Time Facade
 *
 * Stable API surface for care-time Observation write operations.
 * Bridges the UI layer to the @benchmark/core action stack.
 * @agentic connections
 */

import type { MedplumClient } from "@medplum/core";
import type { Observation } from "@medplum/fhirtypes";
import {
  upsertCareTime,
  softDeleteCareTime,
  type UpsertCareTimeInput,
  type UpsertCareTimeResult,
  type SoftDeleteCareTimeInput,
  type SoftDeleteCareTimeResult,
} from "@benchmark/core/actions";
import { findCareTimeForCommunication } from "@benchmark/core/services";

/**
 * Creates a Care-Time facade bound to a MedplumClient. Provides the
 * write + soft-delete + dedup-lookup surface every UI consumer needs.
 * @agentic connections
 */
export function createCareTimeFacade(medplum: MedplumClient) {
  return {
    /**
     * Create or update a care-time Observation.
     * @agentic connections
     */
    upsert: (input: UpsertCareTimeInput): Promise<UpsertCareTimeResult> =>
      upsertCareTime(medplum, input),

    /**
     * Soft-delete a care-time Observation (status=entered-in-error).
     * @agentic connections
     */
    softDelete: (
      input: SoftDeleteCareTimeInput,
    ): Promise<SoftDeleteCareTimeResult> => softDeleteCareTime(medplum, input),

    /**
     * Find an existing care-time Observation derived from a given
     * Communication. Used by the intake-modal "update auto-created
     * intake-call entry" path (D4 no-double-write).
     * @agentic connections
     */
    findForCommunication: (
      communicationId: string,
    ): Promise<Observation | undefined> =>
      findCareTimeForCommunication(medplum, communicationId),
  };
}
